/**
 * OffscreenCanvas transfer helpers + pointer/keyboard coalescing for
 * Contract B (worker remote-runtime) extensions.
 *
 * **Architecture.** Hosts that expose a {@link import("./component-registry").SemanticComponentRegistry}'s
 * `CanvasSurface` / `WebGLSurface` primitive create a host-side `<canvas>`,
 * transfer control to the worker via {@link createOffscreenCanvasTransfer}, and
 * the plugin (running inside the worker realm) calls `offscreen.getContext()`
 * to render. The `<canvas>` is owned by the host DOM tree (so layout, theming,
 * and accessibility properties stay on the host side) but every pixel is
 * produced inside the worker — no per-frame `postMessage` cost, no main-thread
 * blocking.
 *
 * **Pointer / keyboard delivery.** Input events still originate on the host
 * `<canvas>`. To avoid spraying the worker port with per-event traffic the
 * host should wrap pointer-move / wheel / scroll callbacks in
 * {@link createInputEventCoalescer}, matching the trailing-edge coalescer
 * already documented in {@link import("./transport").WorkerRemoteDomTransport}
 * (default 16ms ≈ one rAF). Keyboard events MUST use `discrete: true` —
 * coalescing them would drop characters.
 *
 * **Security.** No capability token, no MCP traffic, and no host-only globals
 * cross via the offscreen transfer. The transfer envelope is plain JSON with a
 * single `OffscreenCanvas` handle in the transfer list — the worker side
 * receives an opaque drawing surface and nothing else.
 */

// ─── Public constants ────────────────────────────────────────────────────────

/**
 * Default trailing-edge coalesce window for pointer / wheel / scroll events,
 * in milliseconds. 16ms ≈ one animation frame at 60Hz. Matches the default
 * documented in {@link import("./transport").WorkerRemoteDomTransport}.
 */
export const DEFAULT_OFFSCREEN_COALESCE_MS = 16;

// ─── Transfer envelope ───────────────────────────────────────────────────────

/**
 * Host → worker envelope shape for an `OffscreenCanvas` transfer. Stable wire
 * contract — the worker side decodes by `type` and uses `surfaceId` to bind
 * the offscreen to the right host slot when a plugin renders multiple
 * canvases concurrently.
 */
export interface OffscreenTransferMessage
{
    type: "ethisys:offscreen:transfer";
    surfaceId: string;
    width: number;
    height: number;
    offscreen: OffscreenCanvas;
}

/**
 * Construction options for {@link createOffscreenCanvasTransfer}.
 */
export interface OffscreenCanvasTransferOptions
{
    /**
     * The host-owned `<canvas>` element. Its `width` / `height` are captured
     * before transfer so the worker sees the intended pixel dimensions even if
     * the host element resizes later (host should send a separate resize
     * message in that case).
     */
    canvas: HTMLCanvasElement;

    /**
     * Stable identifier the worker uses to route the offscreen to the matching
     * surface slot. Typically the SDUI node id of the `CanvasSurface` /
     * `WebGLSurface` primitive that the worker will draw into.
     */
    surfaceId: string;

    /**
     * Host-side postMessage function — typically the
     * {@link import("./transport").WorkerRemoteDomTransport}'s port-side
     * `postMessage`. Tests inject a spy.
     */
    postMessage(message: OffscreenTransferMessage, transfer: Transferable[]): void;
}

/**
 * Successful transfer result. The caller retains a reference to the
 * `OffscreenCanvas` only for observability (e.g., to wire to the worker's
 * reply handshake) — direct rendering from the host side is forbidden because
 * control has already been transferred and any host-side draw call would
 * throw.
 */
export interface OffscreenCanvasTransferResult
{
    offscreen: OffscreenCanvas;
}

/**
 * Module-scoped WeakSet tracking canvases this SDK has already transferred.
 *
 * Stored OUTSIDE the DOM node deliberately:
 *
 * - Writing an SDK expando (e.g. `__ethisysOffscreenTransferred`) onto a host-
 *   owned `<canvas>` mutates state the host doesn't own. A WeakSet keeps the
 *   bookkeeping on the SDK side and lets the canvas object stay pristine.
 * - WeakSet entries are collected when the canvas is GC'd, so the guard does
 *   not leak memory for plugins that mount and unmount many surfaces.
 * - WeakSet membership is identity-based, so React/concurrent remount paths
 *   that re-use the SAME node still see "already transferred"; a fresh DOM
 *   node (always allocated by React when the key changes) is a fresh entry.
 */
const transferredCanvases: WeakSet<HTMLCanvasElement> = new WeakSet<HTMLCanvasElement>();

/**
 * Transfer control of a host-owned `<canvas>` to the worker.
 *
 * Throws if:
 * - the environment lacks `HTMLCanvasElement.prototype.transferControlToOffscreen`
 *   (the helper does NOT silently fall back — Contract B clients must require
 *   the API and surface a clear error in unsupported browsers);
 * - the same canvas has already had control transferred (idempotency is the
 *   caller's responsibility — re-transferring would throw in the browser
 *   anyway, but we raise a clearer, diagnosable message ahead of that).
 */
export function createOffscreenCanvasTransfer(
    options: OffscreenCanvasTransferOptions,
): OffscreenCanvasTransferResult
{
    const { canvas, surfaceId, postMessage } = options;
    const proto = HTMLCanvasElement.prototype as unknown as {
        transferControlToOffscreen?: () => OffscreenCanvas;
    };
    if (typeof proto.transferControlToOffscreen !== "function")
    {
        throw new Error(
            "[offscreen-canvas] OffscreenCanvas is not supported in this environment. " +
            "Contract B canvas surfaces require a browser with HTMLCanvasElement.transferControlToOffscreen.",
        );
    }
    if (transferredCanvases.has(canvas))
    {
        throw new Error(
            `[offscreen-canvas] canvas already transferred (surfaceId='${surfaceId}'). ` +
            "Create a fresh <canvas> for each surface instead of re-transferring.",
        );
    }

    const offscreen = canvas.transferControlToOffscreen();
    transferredCanvases.add(canvas);

    const envelope: OffscreenTransferMessage = {
        type: "ethisys:offscreen:transfer",
        surfaceId,
        width: canvas.width,
        height: canvas.height,
        offscreen,
    };
    postMessage(envelope, [offscreen]);

    return { offscreen };
}

// ─── Coalescing ──────────────────────────────────────────────────────────────

/**
 * Construction options for {@link createInputEventCoalescer}.
 */
export interface InputEventCoalescerOptions
{
    /**
     * Coalesce window in milliseconds. Ignored when {@link discrete} is true.
     * Defaults to {@link DEFAULT_OFFSCREEN_COALESCE_MS} (16ms — one rAF).
     */
    coalesceMs?: number;

    /**
     * When `true`, forward every payload synchronously without coalescing.
     * Use this for keyboard events — coalescing would drop characters by
     * collapsing multiple key strokes into one trailing delivery.
     */
    discrete?: boolean;
}

/**
 * Build a trailing-edge input-event coalescer. The pattern matches
 * {@link import("./transport").WorkerRemoteDomTransport.createCoalescer} —
 * factored out here so OffscreenCanvas callers don't need to construct a full
 * transport just to coalesce pointer-move events.
 *
 * Trailing-edge semantics: every burst within a coalesce window collapses to
 * a single delivery carrying the **last** payload seen in the window. A new
 * payload arriving after a window has flushed starts a fresh window.
 */
export function createInputEventCoalescer<T>(
    sink: (payload: T) => void,
    options: InputEventCoalescerOptions = {},
): (payload: T) => void
{
    if (options.discrete === true)
    {
        // Discrete mode: pass through unchanged. Keyboard input belongs here.
        return (payload: T) =>
        {
            sink(payload);
        };
    }
    const coalesceMs = options.coalesceMs ?? DEFAULT_OFFSCREEN_COALESCE_MS;
    let pending: T | undefined;
    let pendingSet = false;
    let timer: ReturnType<typeof setTimeout> | undefined;

    const flush = (): void =>
    {
        timer = undefined;
        if (!pendingSet)
        {
            return;
        }
        const value = pending as T;
        pending = undefined;
        pendingSet = false;
        sink(value);
    };

    return (payload: T) =>
    {
        pending = payload;
        pendingSet = true;
        if (timer === undefined)
        {
            timer = setTimeout(flush, coalesceMs);
        }
    };
}
